<template>
    <!-- Dashboard do Operador -->
    <operator-stats
        :urgent-count="urgentCashoutCount"
        :remaining-time-formatted="remainingTimeFormatted"
        :mid-day-passed="midDayPassed"
    />

    <div class="mb-4" v-if="showMidDayCutoff || urgentCashoutCount > 0">
        <div
            class="rounded-lg"
            :class="
                midDayPassed
                    ? 'bg-red-50 dark:bg-red-900/20'
                    : 'bg-indigo-50 dark:bg-indigo-900/20'
            "
        >
            <div class="flex items-center justify-between p-4">
                <div class="flex">
                    <ExclamationTriangleIcon
                        class="h-5 w-5"
                        :class="
                            midDayPassed ? 'text-red-500' : 'text-indigo-500'
                        "
                        aria-hidden="true"
                    />
                    <div
                        class="ml-3 text-sm font-medium"
                        :class="
                            midDayPassed
                                ? 'text-red-800 dark:text-red-300'
                                : 'text-indigo-800 dark:text-indigo-300'
                        "
                    >
                        <span class="font-bold">{{
                            midDayPassed ? "Prazo encerrado!" : "Atenção!"
                        }}</span>
                        <span v-if="!midDayPassed">
                            Restam
                            <span class="font-bold">{{ remainingTimeFormatted }}</span>
                            para processar os saques de hoje.
                        </span>
                        <span v-else>
                            O prazo das 12h já passou, mas ainda há saques pendentes.
                        </span>
                        <span class="mt-1 block">
                            Existem
                            <span class="font-bold">{{ urgentCashoutCount }}</span>
                            saques pendentes que
                            {{
                                midDayPassed
                                    ? "deveriam ter sido processados"
                                    : "precisam ser processados"
                            }}
                            até meio-dia.
                        </span>
                    </div>
                </div>
                <div class="flex flex-col gap-2">
                    <button
                        class="rounded-md p-2 text-sm font-medium"
                        :class="
                            midDayPassed
                                ? 'bg-red-100 text-red-800 hover:bg-red-200 dark:bg-red-800 dark:text-red-200 dark:hover:bg-red-700'
                                : 'bg-indigo-100 text-indigo-800 hover:bg-indigo-200 dark:bg-indigo-800 dark:text-indigo-200 dark:hover:bg-indigo-700'
                        "
                        @click="filterUrgentReviews"
                    >
                        {{ isFilteringUrgent ? "Mostrar todos" : "Mostrar apenas urgentes" }}
                    </button>
                </div>
            </div>
        </div>
    </div>

    <filters
        v-model="filters"
        @fetch="onFilterFetch"
        status-filter
        :default-status="['pending']"
        :statuses="[
            { label: 'Solicitado', value: 'pending' },
            { label: 'Aprovado', value: 'approved' },
            { label: 'Reprovado', value: 'rejected' },
        ]"
    >
        <text-input
            placeholder="Min Approved"
            v-model="minApproved"
            type="number"
        />

        <template #actions>
            <div class="flex items-center rounded bg-indigo-700 p-2 text-white shadow">
                {{ pagination.meta.total }}
            </div>
            <button
                class="ml-2 flex items-center gap-1 rounded-md bg-indigo-600 p-2 text-sm font-medium text-white hover:bg-indigo-700"
                @click="filterUrgentReviews"
                v-tooltip="'Saques que precisam ser processados até 12h de hoje'"
            >
                <ClockIcon class="h-5 w-5" />
                {{ isFilteringUrgent ? "Todos" : "Até 12h" }}
            </button>
        </template>
    </filters>

    <div class="space-y-1">
        <empty-state v-if="!loading && reviews.length < 1" />
        <div v-else>
            <review-decision-panel
                ref="reviewDecisionPanel"
                :review="selectedReview"
                :payable-details="selectedPayableDetails"
                :checklist="decisionChecklist"
                :is-checklist-complete="isChecklistComplete"
                :is-same-day-urgent="selectedReview ? isSameDayUrgent(selectedReview) : false"
                :action-processing="actionProcessing"
                @toggle-checklist="toggleChecklistItem"
                @reset-checklist="resetChecklist"
                @approve="approveSelected"
                @reject="rejectSelected"
                @refresh="handleReviewCompleted"
            />
        </div>
    </div>
</template>

<script>
import { ClockIcon } from "@heroicons/vue/20/solid";
import { ExclamationTriangleIcon } from "@heroicons/vue/24/outline";
import Filters from "../../components/layouts/Filters.vue";
import EmptyState from "../../components/layouts/EmptyState.vue";
import TextInput from "../../components/forms/TextInput.vue";
import OperatorStats from "../../components/cashouts/reviews/OperatorStats.vue";
import ReviewDecisionPanel from "../../components/cashouts/reviews/ReviewDecisionPanel.vue";
import { mapWritableState } from "pinia";
import { Store } from "../../stores/store.js";
import { buildChecklistItems } from "../../utils/reviewChecklist.js";
import axios from "axios";

export default {
    name: "ReviewsCockpit",
    components: {
        ReviewDecisionPanel,
        OperatorStats,
        TextInput,
        EmptyState,
        ExclamationTriangleIcon,
        Filters,
        ClockIcon,
    },
    data() {
        return {
            loading: false,
            loadingUrgentCount: false,
            urgentCashoutCount: 0,
            reviews: [],
            selectedReviewId: null,
            decisionChecklist: [],
            filters: {},
            minApproved: "",
            isFilteringUrgent: false,
            remainingTime: 0,
            midDayPassed: false,
            timerInterval: null,
            pagination: {
                meta: {
                    current_page: 1,
                    last_page: 10,
                    page: 1,
                    per_page: 10,
                    total: 0,
                },
            },
            payableDetails: {},
            actionProcessing: false,
        };
    },
    computed: {
        ...mapWritableState(Store, ["authUser"]),
        remainingTimeFormatted() {
            if (this.midDayPassed) {
                return "00:00:00";
            }

            const hours = Math.floor(this.remainingTime / 3600);
            const minutes = Math.floor((this.remainingTime % 3600) / 60);
            const seconds = this.remainingTime % 60;

            return [
                hours.toString().padStart(2, "0"),
                minutes.toString().padStart(2, "0"),
                seconds.toString().padStart(2, "0"),
            ].join(":");
        },
        showMidDayCutoff() {
            const now = new Date();
            const day = now.getDay();
            return day >= 1 && day <= 5;
        },
        orderedReviews() {
            return [...this.reviews].sort((a, b) => {
                const urgentDiff = (this.isSameDayUrgent(b) ? 0 : 1) - (this.isSameDayUrgent(a) ? 0 : 1);
                if (urgentDiff !== 0) {
                    return urgentDiff;
                }

                const scoreA = a?.seller?.score ?? 100;
                const scoreB = b?.seller?.score ?? 100;
                if (scoreA !== scoreB) {
                    return scoreA - scoreB;
                }

                const dateA = new Date(a.created_at).getTime();
                const dateB = new Date(b.created_at).getTime();
                return dateA - dateB;
            });
        },
        selectedReview() {
            if (!this.selectedReviewId) {
                return null;
            }

            return this.reviews.find((review) => review.id === this.selectedReviewId) || null;
        },
        selectedPayableDetails() {
            if (!this.selectedReview) {
                return null;
            }

            return this.payableDetails[this.selectedReview.cashout.id] || null;
        },
        isChecklistComplete() {
            if (!this.decisionChecklist.length) {
                return false;
            }

            return this.decisionChecklist.every((item) => item.checked);
        },
    },
    watch: {
        selectedReview(newReview, oldReview) {
            if (!newReview) {
                this.decisionChecklist = [];
                return;
            }

            const preserve = newReview?.id === oldReview?.id;
            this.setChecklist(newReview, this.selectedPayableDetails, { preserve });
        },
        selectedPayableDetails(newDetails) {
            if (this.selectedReview) {
                this.setChecklist(this.selectedReview, newDetails, { preserve: true });
            }
        },
    },
    mounted() {
        this.filters.status = ["pending"];
        this.startMidDayTimer();
        setTimeout(() => {
            this.fetch();
            this.fetchUrgentCount();
        }, 500);
    },
    beforeDestroy() {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
        }
    },
    methods: {
        onFilterFetch() {
            this.pagination.meta.current_page = 1;
            this.actionProcessing = true;
            this.fetch().finally(() => {
                this.actionProcessing = false;
            });
        },
        startMidDayTimer() {
            let lastCountUpdate = 0;

            const updateTimer = () => {
                const now = new Date();
                const midDay = new Date(
                    now.getFullYear(),
                    now.getMonth(),
                    now.getDate(),
                    12,
                    0,
                    0,
                );

                if (now >= midDay) {
                    this.midDayPassed = true;
                    this.remainingTime = 0;
                } else {
                    this.midDayPassed = false;
                    this.remainingTime = Math.floor((midDay - now) / 1000);
                }

                const currentTime = Math.floor(Date.now() / 1000);
                if (
                    (this.midDayPassed || this.remainingTime < 1800) &&
                    currentTime - lastCountUpdate > 60
                ) {
                    this.fetchUrgentCount();
                    lastCountUpdate = currentTime;
                }
            };

            updateTimer();
            this.timerInterval = setInterval(updateTimer, 1000);
        },
        async fetch() {
            this.loading = true;

            const requestFilters = {
                ...this.filters,
                "filter[minApproved]": this.minApproved,
            };

            if (this.isFilteringUrgent) {
                requestFilters["filter[urgentMidDay]"] = true;
            }

            try {
                const { data } = await axios.get("/api/cashout/review", {
                    params: {
                        page: this.pagination.meta.current_page,
                        per_page: this.pagination.meta.per_page,
                        ...requestFilters,
                    },
                });

                this.reviews = data.data;
                this.pagination = data;
                this.ensureSelection();

                if (this.selectedReview) {
                    await this.loadPayableDetails(this.selectedReview);
                }
            } catch (error) {
                console.error("❌ [ReviewsCockpit] Erro ao buscar reviews:", error);
            } finally {
                this.loading = false;
            }
        },
        fetchUrgentCount() {
            this.loadingUrgentCount = true;

            axios
                .get("/api/cashout/review/count", {
                    params: {
                        "filter[status]": "pending",
                    },
                })
                .then(({ data }) => {
                    this.urgentCashoutCount = data.count || 0;
                })
                .catch((error) => {
                    console.error("Erro ao buscar contagem de saques urgentes:", error);

                    if (this.isFilteringUrgent) {
                        this.urgentCashoutCount = this.pagination.meta.total;
                    }
                })
                .finally(() => {
                    this.loadingUrgentCount = false;
                });
        },
        filterUrgentReviews() {
            this.isFilteringUrgent = !this.isFilteringUrgent;
            this.pagination.meta.current_page = 1;
            this.actionProcessing = true;
            this.fetch()
                .finally(() => {
                    this.actionProcessing = false;
                    setTimeout(() => this.fetchUrgentCount(), 500);
                });
        },
        isSameDayUrgent(review) {
            const reviewDate = new Date(review.created_at);
            const now = new Date();
            const today = new Date(
                now.getFullYear(),
                now.getMonth(),
                now.getDate(),
            );
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);

            const reviewDay = reviewDate.getDay();
            const reviewHour = reviewDate.getHours();

            if (reviewDate >= today && reviewHour < 12) {
                return true;
            }

            if (reviewDate >= yesterday && reviewHour >= 12) {
                const yesterdayDay = yesterday.getDay();
                return yesterdayDay >= 1 && yesterdayDay <= 4;
            }

            if (reviewDate >= yesterday) {
                const dayOfWeek = reviewDate.getDay();

                if (dayOfWeek === 5 && reviewHour >= 12) {
                    return true;
                }

                if (dayOfWeek === 6 || dayOfWeek === 0) {
                    return true;
                }
            }

            return false;
        },
        loadPayableDetails(review) {
            if (!review || !review.cashout) {
                return Promise.resolve(null);
            }

            const cashoutId = review.cashout.id;

            if (this.payableDetails[cashoutId]) {
                return Promise.resolve(this.payableDetails[cashoutId]);
            }

            return axios
                .get(`/api/cashout/${cashoutId}/payables/details`)
                .then(({ data }) => {
                    this.payableDetails = {
                        ...this.payableDetails,
                        [cashoutId]: data,
                    };
                    return data;
                })
                .catch((error) => {
                    console.error(
                        `❌ [ReviewsCockpit] Erro ao buscar detalhes do cashout ${cashoutId}:`,
                        error,
                    );
                    return null;
                });
        },
        ensureSelection() {
            if (!this.reviews.length) {
                this.selectedReviewId = null;
                return;
            }

            const stillSelected = this.reviews.some(
                (review) => review.id === this.selectedReviewId,
            );

            if (!stillSelected) {
                const nextReview = this.orderedReviews[0];
                this.selectedReviewId = nextReview ? nextReview.id : null;
            }
        },
        selectReview(review) {
            if (!review) {
                return;
            }

            this.selectedReviewId = review.id;
            this.loadPayableDetails(review);
        },
        setChecklist(review, details, { preserve } = { preserve: false }) {
            const previousState = new Map(
                this.decisionChecklist.map((item) => [item.id, item.checked]),
            );

            const items = buildChecklistItems(review, details, {
                includeSla: this.isSameDayUrgent(review),
            });

            this.decisionChecklist = items.map((item) => ({
                ...item,
                checked: preserve ? previousState.get(item.id) ?? item.checked : false,
            }));
        },
        toggleChecklistItem(itemId) {
            const item = this.decisionChecklist.find(
                (checkItem) => checkItem.id === itemId,
            );

            if (item) {
                item.checked = !item.checked;
            }
        },
        resetChecklist() {
            if (this.selectedReview) {
                this.setChecklist(this.selectedReview, this.selectedPayableDetails, {
                    preserve: false,
                });
            }
        },
        approveSelected() {
            if (this.selectedReview && !this.actionProcessing) {
                this.quickApprove(this.selectedReview);
            }
        },
        rejectSelected() {
            if (this.selectedReview && !this.actionProcessing) {
                this.quickReject(this.selectedReview);
            }
        },
        async handleReviewCompleted(review) {
            const processedReview = review || this.selectedReview;
            this.actionProcessing = true;

            if (processedReview && this.isSameDayUrgent(processedReview)) {
                this.urgentCashoutCount = Math.max(
                    0,
                    this.urgentCashoutCount - 1,
                );
            }

            try {
                await this.fetch();
                this.fetchUrgentCount();

                // Reload SmartAnalysis to get updated AI score
                if (this.$refs.reviewDecisionPanel) {
                    await this.$refs.reviewDecisionPanel.reloadSmartAnalysis();
                    // Also reload the cashout checklist to get fresh data
                    await this.$refs.reviewDecisionPanel.reloadCashoutChecklist();
                }
            } finally {
                this.actionProcessing = false;
            }
        },
        async quickApprove(review) {
            if (!review) {
                return;
            }

            this.actionProcessing = true;

            try {
                await axios.post(`/api/cashout/${review.cashout.id}/review`, {
                    status: "approved",
                    content: "Aprovado automaticamente - baixo risco",
                    user_id: this.authUser?.id,
                });

                if (this.isSameDayUrgent(review)) {
                    this.urgentCashoutCount = Math.max(
                        0,
                        this.urgentCashoutCount - 1,
                    );
                }

                await this.fetch();
                this.fetchUrgentCount();

                // Reload SmartAnalysis to get updated AI score
                if (this.$refs.reviewDecisionPanel) {
                    await this.$refs.reviewDecisionPanel.reloadSmartAnalysis();
                    // Also reload the cashout checklist to get fresh data
                    await this.$refs.reviewDecisionPanel.reloadCashoutChecklist();
                }
            } catch (error) {
                console.error(error);
            } finally {
                this.actionProcessing = false;
            }
        },
        async quickReject(review) {
            if (!review || this.actionProcessing) {
                return;
            }

            const reason = prompt("Motivo da rejeição:");
            if (!reason) return;

            this.actionProcessing = true;

            try {
                await axios.post(`/api/cashout/${review.cashout.id}/review`, {
                    status: "rejected",
                    content: reason,
                    user_id: this.authUser?.id,
                });

                if (this.isSameDayUrgent(review)) {
                    this.urgentCashoutCount = Math.max(
                        0,
                        this.urgentCashoutCount - 1,
                    );
                }

                await this.fetch();
                this.fetchUrgentCount();

                // Reload SmartAnalysis to get updated AI score
                if (this.$refs.reviewDecisionPanel) {
                    await this.$refs.reviewDecisionPanel.reloadSmartAnalysis();
                    // Also reload the cashout checklist to get fresh data
                    await this.$refs.reviewDecisionPanel.reloadCashoutChecklist();
                }
            } catch (error) {
                console.error(error);
            } finally {
                this.actionProcessing = false;
            }
        },
    },
};
</script>
